'use client'

import { useState, Suspense, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useWidgetGlobalOperations } from '@/modules/global/hooks/useWidgetGlobalOperations';

import { useDashboard } from '@/modules/dashboard';
import { getPageDisplayTitle } from '@/modules/pages/utils/products';
import { Page, usePage } from '@/modules/pages';
import { useWidget, Widget } from '@/modules/widgets';

import { DashboardPage } from '@/components/organisms/DashboardGridPage';
import { NewPageEmptyState } from '@/components/organisms/DashboardGridPage/NewPageEmptyState';
import { GeneratingPageState } from '@/components/organisms/DashboardGridPage/GeneratingPageState';

import { config } from '@/config/env';
import { DashboardPageSubHeader } from '@/components/organisms/DashboardPageSubHeader';
import { SpinningLogo } from '@/components/animations';

import './dashboard-demo.css';
import { AddWidgetDrawer } from '@/components/organisms/AddWidgetDrawer';
import { useEditWidget } from '@/modules/widgets/context/EditWidgetContext';
import { Modal } from '@/components/molecules/Modal/Modal';
import { useModalController } from '@/components/molecules/Modal/useModalController';
import { PhoneVerificationModalView } from '@/components/organisms/PhoneVerificationModal/PhoneVerificationModalView';
import { useAuth } from '@/modules/auth/context/UserContext';
import { useProfile } from '@/modules/profile/hooks/useProfile';

function Dashboard() {
    const searchParams = useSearchParams();
    const dashboardId = searchParams.get('dashboardId') || '1';
    const pageId = searchParams.get('pageId');

    const { state: widgetState } = useWidget();
    const { state: dashboardState } = useDashboard();
    const { state: pageState } = usePage();
    const { hydrated, user } = useAuth();
    const { updateProfile, userPreferences, profileData, error: profileError } = useProfile();
    const { isOpen: isEditDrawerOpen, widget: editingWidget, isDuplicating, closeEditDrawer } = useEditWidget();

    const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
    const [restoredGridContent, setRestoredGridContent] = useState<number[][] | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState<string>('');
    const [feedbackType] = useState<'success' | 'error'>('success');

    const { loading: isLoading, error } = widgetState;
    const { currentDashboard } = dashboardState;
    const { currentPage } = pageState;

    const { addWidget, updateWidget } = useWidgetGlobalOperations();
    const displayTitle = getPageDisplayTitle(currentPage?.metadata?.['page-titles'], dashboardState.dealerContractedProducts?.contractedProducts ?? []) ?? currentPage?.title ?? 'Summary';
    const activeDashboardId = dashboardId || config.DASHBOARD_ID.toString();
    const phoneModalController = useModalController();
    const [phonePromptDismissedSession, setPhonePromptDismissedSession] = useState(false);
    const [phoneSubmitLoading, setPhoneSubmitLoading] = useState(false);
    const phonePromptOpenedRef = useRef(false);

    const dashboardSurfaceReady =
        !isLoading && !!currentDashboard && !pageState.generatingPage;
    const showPhoneVerificationPrompt =
        config.POPUP_PHONE_NUMBER_ACTIVE &&
        hydrated &&
        !phonePromptDismissedSession &&
        userPreferences?.showInputPhonePopUp === true;

    useEffect(() => {
        if (!showPhoneVerificationPrompt || phonePromptOpenedRef.current) return;
        if (!dashboardSurfaceReady) return;
        phonePromptOpenedRef.current = true;
        phoneModalController.open();
    }, [dashboardSurfaceReady, showPhoneVerificationPrompt, phoneModalController]);

    const handlePhoneSubmit = useCallback(async (_phoneNumber: string) => {
        setPhoneSubmitLoading(true);
        try {
            await updateProfile(profileData?.UserID ?? user?.userId ?? "", { profileData: { UserPhone: _phoneNumber }, userPreferences: { showInputPhonePopUp: false } });

            phoneModalController.close();
            setPhonePromptDismissedSession(true);
        } catch (error) {
            console.error(error);
        } finally {
            setPhoneSubmitLoading(false);
        }
    }, [phoneModalController]);

    const handlePhoneSkip = useCallback(async () => {
        setPhoneSubmitLoading(true);
        try {
            await updateProfile(profileData?.UserID ?? user?.userId ?? "", { userPreferences: { showInputPhonePopUp: false } });

            phoneModalController.close();
            setPhonePromptDismissedSession(true);
        } catch (error) {
            console.error(error);
        } finally {
            setPhoneSubmitLoading(false);
        }
    }, [phoneModalController]);

    const phoneVerificationModal = showPhoneVerificationPrompt ? (
        <Modal
            title="Phone Verification"
            controller={phoneModalController}
            overlayBlurEffect={true}
            closeOnOverlayClick={false}
            closeOnEsc={false}
            hideActionButtons={true}
            hideHeader={true}
            contentClassName="!p-6"
        >
            <PhoneVerificationModalView
                error={profileError ? profileError : undefined}
                controller={phoneModalController}
                isLoading={phoneSubmitLoading}
                initialPhone={profileData?.UserPhone ?? ""}
                onSubmit={handlePhoneSubmit}
                onSkip={handlePhoneSkip}
            />
        </Modal>
    ) : null;

    const handleCreateWidget = async (prompt: string) => {
        setIsCreating(true);
    };

    const handleWidgetCreated = useCallback((widget: Widget) => {
        setIsCreateDrawerOpen(false);
        setIsCreating(false);
        if (isDuplicating) {
            closeEditDrawer();
        }
        addWidget(widget);
    }, [addWidget, isDuplicating, closeEditDrawer]);

    const handleWidgetUpdated = useCallback((widget: Widget) => {
        closeEditDrawer();
        updateWidget(widget);
    }, [closeEditDrawer, updateWidget]);

    const handleNewWidget = () => {
        setIsCreateDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        if (isEditDrawerOpen) {
            closeEditDrawer();
        } else {
            setIsCreateDrawerOpen(false);
            setFeedbackMessage('');
            setIsCreating(false);
        }
    };


    if (isLoading || !currentDashboard) {
        return (
            <div className="flex-1 flex items-center justify-center min-h-[50vh]">
                <div className="flex flex-col items-center justify-center">
                    <SpinningLogo size={64} />
                    <p className="text-gray-600 mt-4" style={{ fontFamily: 'Roboto, sans-serif' }}>Loading dashboard</p>
                </div>
            </div>
        );
    }

    if (pageState.generatingPage) {
        return (
            <div className="flex-1 h-auto flex flex-col items-center" data-pdf-content>
                <GeneratingPageState />
            </div>
        );
    }

    if (pageState.pendingNewPage) {
        return (
            <div className="flex-1 h-auto flex flex-col items-center" data-pdf-content>
                <div className="flex-1 h-auto container relative z-0">
                    <NewPageEmptyState onAddWidget={handleNewWidget} />
                </div>

                <AddWidgetDrawer
                    isOpen={isEditDrawerOpen || isCreateDrawerOpen}
                    onClose={handleDrawerClose}
                    onSubmit={handleCreateWidget}
                    onWidgetCreated={handleWidgetCreated}
                    onWidgetUpdated={handleWidgetUpdated}
                    isLoading={isCreating}
                    feedbackMessage={feedbackMessage}
                    feedbackType={feedbackType}
                    editWidget={editingWidget || undefined}
                    isDuplicating={isDuplicating}
                />
                {phoneVerificationModal}
            </div>
        );
    }

    return (
        <>
            <div className="flex-1 h-auto flex flex-col items-center" data-pdf-content>
                <DashboardPageSubHeader title={displayTitle} onAddWidget={handleNewWidget} onRestoreDefault={setRestoredGridContent} />


                {currentDashboard && (

                    <div className="flex-1 h-auto container relative z-0">
                        <DashboardPage
                            page={currentPage as Page}
                            onAddWidget={handleNewWidget}
                            restoredGridContent={restoredGridContent}
                            onRestoredGridContentConsumed={() => setRestoredGridContent(null)}
                        />

                    </div>
                )}
            </div>

            {/* Unified Drawer - handles both create and edit modes */}
            <AddWidgetDrawer
                isOpen={isEditDrawerOpen || isCreateDrawerOpen}
                onClose={handleDrawerClose}
                onSubmit={handleCreateWidget}
                onWidgetCreated={handleWidgetCreated}
                onWidgetUpdated={handleWidgetUpdated}
                isLoading={isCreating}
                feedbackMessage={feedbackMessage}
                feedbackType={feedbackType}
                editWidget={editingWidget || undefined}
                isDuplicating={isDuplicating}
            />
            {phoneVerificationModal}
        </>
    );
}


export default function DashboardWrapper() {
    return (
        <Suspense fallback={
            <div className="flex h-screen bg-[var(--main-background)]">
                <div className="flex-1 flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center">
                        <SpinningLogo size={64} />
                        <p className="text-gray-600 mt-4" style={{ fontFamily: 'Roboto, sans-serif' }}>Loading dashboard</p>
                    </div>
                </div>
            </div>
        }>
            <Dashboard />
        </Suspense>
    );
}